import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, Select, MenuItem, TextField, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { WriteButton, WriteIconButton } from '../../ui';

/**
 * Editor for the platform's subsystem-routing attributes — `walletSubsystem`
 * and `taskSubsystem`:
 *
 *   { "default": "v2", "planYearOverrides": { "2027": "v2" } }
 *
 * The platform resolves a version per (tenant, plan year): the override for that
 * plan year wins, otherwise `default`. Authoring this as raw JSON is easy to get
 * wrong (a typo'd key silently falls back to v1), so this renders the default as
 * a picker and the overrides as year → version rows.
 *
 * Unknown sibling keys in the stored object are preserved untouched, so a future
 * platform addition is never dropped by an edit here.
 */

const VERSION_OPTIONS = ['v1', 'v2'] as const;

interface SubsystemValue {
  default?: string;
  planYearOverrides?: Record<string, string>;
  [extra: string]: unknown;
}

interface OverrideRow {
  year: string;
  version: string;
}

interface Props {
  label: string;
  description?: string;
  value: unknown;
  onChange: (v: unknown) => void;
}

const asObject = (value: unknown): SubsystemValue =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as SubsystemValue) : {};

const rowsFromValue = (value: unknown): OverrideRow[] => {
  const overrides = asObject(value).planYearOverrides;
  if (!overrides || typeof overrides !== 'object') return [];
  return Object.entries(overrides).map(([year, version]) => ({ year: String(year), version: String(version) }));
};

/** rows → planYearOverrides. Blank years are dropped; a repeated year keeps the last row. */
const rowsToOverrides = (rows: OverrideRow[]): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const r of rows) {
    const year = r.year.trim();
    if (!year) continue;
    out[year] = r.version;
  }
  return out;
};

const SubsystemVersionField: React.FC<Props> = ({ label, description, value, onChange }) => {
  const current = asObject(value);
  const [rows, setRows] = useState<OverrideRow[]>(() => rowsFromValue(value));
  // What we last emitted, so an external change (tenant switch, revert) re-seeds
  // the rows while our own emissions do not fight the user's in-progress typing.
  const lastEmitted = useRef<string>(JSON.stringify(rowsToOverrides(rowsFromValue(value))));

  useEffect(() => {
    const incoming = JSON.stringify(asObject(value).planYearOverrides ?? {});
    if (incoming !== lastEmitted.current) {
      setRows(rowsFromValue(value));
      lastEmitted.current = incoming;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(asObject(value).planYearOverrides ?? {})]);

  const emit = useCallback((patch: Partial<SubsystemValue>, nextRows?: OverrideRow[]) => {
    const next: SubsystemValue = { ...current, ...patch };
    if (nextRows) {
      const overrides = rowsToOverrides(nextRows);
      lastEmitted.current = JSON.stringify(overrides);
      if (Object.keys(overrides).length > 0) next.planYearOverrides = overrides;
      else delete next.planYearOverrides;
    }
    onChange(next);
  }, [current, onChange]);

  const setRowsAndEmit = useCallback((nextRows: OverrideRow[]) => {
    setRows(nextRows);
    emit({}, nextRows);
  }, [emit]);

  const duplicateYears = useMemo(() => {
    const seen = new Set<string>();
    const dupes = new Set<string>();
    for (const r of rows) {
      const y = r.year.trim();
      if (!y) continue;
      if (seen.has(y)) dupes.add(y);
      seen.add(y);
    }
    return dupes;
  }, [rows]);

  const defaultVersion = typeof current.default === 'string' ? current.default : '';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#141417', fontFamily: 'Manrope, sans-serif' }}>{label}</Typography>
        {defaultVersion && (
          <Chip label={`default ${defaultVersion}`} size="small"
            sx={{ height: 18, fontSize: 10, fontWeight: 700, backgroundColor: defaultVersion === 'v2' ? '#DCFCE7' : '#EFF0F0' }} />
        )}
      </Box>
      {description && <Typography sx={{ fontSize: 11, color: '#6F6F6F', mb: 1 }}>{description}</Typography>}

      {/* Default version — applies to every plan year without an override */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Typography sx={{ fontSize: 12, color: '#4A546A', minWidth: 92 }}>Default version</Typography>
        <Select size="small" displayEmpty value={defaultVersion}
          onChange={(e) => emit({ default: e.target.value })}
          sx={{ fontSize: 13, borderRadius: '6px', minWidth: 140 }}>
          <MenuItem value="" sx={{ fontSize: 13, fontStyle: 'italic' }}>Not set (platform default)</MenuItem>
          {VERSION_OPTIONS.map((v) => <MenuItem key={v} value={v} sx={{ fontSize: 13 }}>{v}</MenuItem>)}
        </Select>
      </Box>

      {/* Per-plan-year overrides */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
        <Typography sx={{ fontSize: 12, color: '#4A546A' }}>
          Plan year overrides {rows.length > 0 && <span style={{ color: '#A1A1A1' }}>({rows.length})</span>}
        </Typography>
        <WriteButton size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />}
          onClick={() => setRowsAndEmit([...rows, { year: '', version: 'v2' }])}
          sx={{ textTransform: 'none', fontSize: 12, color: '#0078B3' }}>
          Add year
        </WriteButton>
      </Box>

      {rows.length === 0 ? (
        <Typography sx={{ fontSize: 11, color: '#A1A1A1', fontStyle: 'italic' }}>
          No overrides — every plan year uses the default.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {rows.map((row, idx) => {
            const year = row.year.trim();
            const invalidYear = year !== '' && !/^\d{4}$/.test(year);
            return (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <TextField size="small" placeholder="2027" value={row.year}
                  onChange={(e) => {
                    const next = [...rows];
                    next[idx] = { ...next[idx], year: e.target.value };
                    setRowsAndEmit(next);
                  }}
                  error={invalidYear || duplicateYears.has(year)}
                  helperText={invalidYear ? '4-digit plan year' : duplicateYears.has(year) ? 'duplicate year — last row wins' : ' '}
                  sx={{ width: 120 }}
                  InputProps={{ sx: { fontSize: 13, borderRadius: '6px' } }}
                  FormHelperTextProps={{ sx: { fontSize: 10, mx: 0.5 } }} />
                <Select size="small" value={row.version}
                  onChange={(e) => {
                    const next = [...rows];
                    next[idx] = { ...next[idx], version: e.target.value };
                    setRowsAndEmit(next);
                  }}
                  sx={{ fontSize: 13, borderRadius: '6px', minWidth: 110 }}>
                  {VERSION_OPTIONS.map((v) => <MenuItem key={v} value={v} sx={{ fontSize: 13 }}>{v}</MenuItem>)}
                </Select>
                <WriteIconButton size="small" onClick={() => setRowsAndEmit(rows.filter((_, i) => i !== idx))}
                  sx={{ color: '#E53935', mt: 0.5 }}>
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </WriteIconButton>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default SubsystemVersionField;
